import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { EmptyState } from '@/shared/ui/EmptyState'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { NamedCode } from '@/features/notify/NamedCode'
import { templateName } from '@/features/notify/notifyLabels'
import { TemplateFormModal, type TemplateGroup } from '@/features/notify/TemplateFormModal'
import {
  CHANNELS,
  TEMPLATE_LOCALES,
  deleteTemplate,
  fetchTemplates,
  type TemplateView,
} from '@/features/notify/notifyApi'

/** `(code, channel)` juftligi bo'yicha guruhlaydi — bitta qatorda uch til birga ko'rinsin */
function groupTemplates(rows: readonly TemplateView[]): TemplateGroup[] {
  const map = new Map<string, TemplateGroup>()
  for (const row of rows) {
    const key = `${row.code}|${row.channel}`
    let group = map.get(key)
    if (!group) {
      group = { code: row.code, channel: row.channel, byLocale: {} }
      map.set(key, group)
    }
    if (row.locale === 'uz' || row.locale === 'ru' || row.locale === 'en') {
      group.byLocale[row.locale] = row
    }
  }
  return [...map.values()].sort((a, b) => a.code.localeCompare(b.code) || a.channel.localeCompare(b.channel))
}

/**
 * Shablonlar — uz/ru/en bir ekranda (task talabi, `notifyApi.ts`ga qarang).
 *
 * <h2>⚠️ `DataTable` EMAS va SAHIFALASH ham YO'Q</h2>
 * `GET /api/notify/templates` (`TemplateResource#list`) faqat ixtiyoriy
 * `code` parametrini biladi — `page`, `size` yoki saralash YO'Q, javob
 * esa TO'LIQ ro'yxat. `DataTable` server sahifalashini talab qiladi.
 *
 * <p>Ilgari bu yerda `pagination={{ current, pageSize }}` turardi:
 * server butun ro'yxatni yuborardi, panel esa uni MIJOZDA kesib
 * "sahifalash bor" degan taassurot berardi. Bu ko'zbo'yamachilik —
 * ro'yxat kattalashganda so'rov ham, xotira ham o'sib boraveradi va
 * buni hech kim ko'rmaydi. Shuning uchun sahifalash OLIB TASHLANDI.
 *
 * <h2>⚠️ QIDIRUV VA FILTR — MIJOZDA, LEKIN YOLG'ONSIZ</h2>
 * Ular TO'LIQ ro'yxat ustidan yuradi (yuqoridagi sabab), ya'ni hech
 * qanday qator yashirinib qolmaydi. Holat MANZILDA — sahifa
 * yangilanganda filtr yo'qolmaydi.
 */
export function NotifyTemplatesPage() {
  const { t } = useTranslation()
  /*
    ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm`
    `ConfigProvider` mavzusiga yetmaydi va qorong'i panel ustida OQ
    dialog chizadi (o'lchov `SettingsPage` dagi izohda).
  */
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<TemplateGroup | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const channelFilter = tableState.filters['channel']?.[0]
  const query = tableState.search.trim().toLowerCase()

  const templates = useQuery({ queryKey: queryKeys.notify.templates, queryFn: fetchTemplates })
  const allGroups = useMemo(() => groupTemplates(templates.data ?? []), [templates.data])
  const groups = useMemo(
    () =>
      allGroups.filter((group) => {
        if (channelFilter !== undefined && group.channel !== channelFilter) return false
        if (query === '') return true
        return [group.code, templateName(group.code, t) ?? ''].some((text) => text.toLowerCase().includes(query))
      }),
    [allGroups, channelFilter, query, t],
  )
  const isFiltered = query !== '' || channelFilter !== undefined

  const removeGroup = useMutation({
    mutationFn: async (group: TemplateGroup) => {
      const ids = TEMPLATE_LOCALES.map((locale) => group.byLocale[locale]?.id).filter(
        (id): id is string => id !== undefined,
      )
      await Promise.all(ids.map((id) => deleteTemplate(id)))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.templates })
      // X-SIB-004 — o'chirishdan keyin qator jadvaldan yo'qoladi, lekin
      // "o'chdimi yoki filtr shundaymi" degan savol qolardi
      void message.success(t('common.toastDeleted'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<TemplateGroup> = [
    {
      // ⚠️ Sahifalash YO'Q (sinf javadoc'i), ya'ni `index` allaqachon
      //    to'liq ro'yxatdagi o'rin — siljish hisoblanmaydi.
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'code',
      title: t('notify.template.code'),
      width: 220,
      render: (_, row) => <NamedCode name={templateName(row.code, t)} code={row.code} />,
    },
    {
      /*
        ⚠️ Ustun ichidagi VORONKA olib tashlandi, filtr esa jadval
        USTIGA — ko'rinadigan joyga — chiqarildi. Sabab
        `SubscriberFilterBar` da o'lchangan: sarlavhadagi kichik
        voronka texnik jihatdan ishlaydi, amalda esa topilmaydi
        ("filtrlar yo'q" degan shikoyat aynan shundan chiqqan).

        ⚠️ Ikkinchi sabab — HOLAT: voronka o'z ichida holat saqlaydi
        va u URL'ga tushmaydi, ya'ni sahifa yangilanganda yo'qolardi.
      */
      key: 'channel',
      title: t('notify.channel'),
      width: 130,
      render: (_, row) => <Tag>{t(`notify.channelName.${row.channel}`)}</Tag>,
    },
    ...TEMPLATE_LOCALES.map((locale) => ({
      key: locale,
      title: t(`language.${locale}`),
      width: 90,
      align: 'center' as const,
      render: (_: unknown, row: TemplateGroup) =>
        row.byLocale[locale] ? (
          <Tooltip title={row.byLocale[locale]?.body}>
            <CheckCircleOutlined style={{ color: 'green' }} />
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    })),
    {
      key: 'updatedAt',
      title: t('notify.template.updatedAt'),
      width: 170,
      render: (_, row) => {
        const latest = TEMPLATE_LOCALES.map((l) => row.byLocale[l]?.updatedAt).filter(
          (v): v is string => Boolean(v),
        ).sort().at(-1)
        return latest ? (
          <Typography.Text title={utcTooltip(latest)}>{formatDateTime(latest)}</Typography.Text>
        ) : (
          '—'
        )
      },
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'notify.template.write',
            },
            {
              key: 'delete',
              label: t('actions.delete'),
              onClick: () => {
                modal.confirm({
                  title: t('notify.template.deleteTitle'),
                  content: t('notify.template.deleteConfirm', { code: row.code }),
                  okButtonProps: { danger: true },
                  okText: t('actions.delete'),
                  cancelText: t('common.cancel'),
                  onOk: () => removeGroup.mutateAsync(row),
                })
              },
              permission: 'notify.template.write',
              danger: true,
              divider: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.notifyTemplates')}
        description={t('pageIntro.notifyTemplates')}
        extra={
          <Can permission="notify.template.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('notify.template.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* ⚠️ Ro'yxat XATOSI ko'rinadi: bo'sh jadval "shablon yo'q" bilan
          "shablonlarni olib bo'lmadi" ni ajratmasdi. */}
      {templates.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dataTable.loadFailed')}
          description={translateError(templates.error, t)}
        />
      ) : null}

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <UrlSearchInput placeholder={t('notify.template.searchPlaceholder')} />
          <Select
            allowClear
            style={{ minWidth: 170 }}
            placeholder={t('notify.channel')}
            value={channelFilter}
            onChange={(v?: string) =>
              patchTable({ filters: { ...tableState.filters, channel: v ? [v] : [] } })
            }
            options={CHANNELS.map((c) => ({ value: c, label: t(`notify.channelName.${c}`) }))}
          />
          {isFiltered ? (
            <Space size={4}>
              <Tag color="blue">
                {t('roles.shownOfTotal', { shown: groups.length, total: allGroups.length })}
              </Tag>
              <Button size="small" type="link" onClick={resetTable}>
                {t('dataTable.clearFilters')}
              </Button>
            </Space>
          ) : null}
        </Space>

        <Table<TemplateGroup>
          columns={columns}
          dataSource={groups}
          rowKey={(row) => `${row.code}|${row.channel}`}
          loading={templates.isPending}
          size="middle"
          scroll={{ x: 1000 }}
          // ⚠️ SAHIFALASH YO'Q — sabab sinf javadoc'ida (endpoint
          //    `page`/`size` ni qabul qilmaydi, soxta sahifalash esa
          //    taqiqlangan).
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                variant="no-data"
                title={isFiltered ? t('dataTable.emptyFiltered') : t('notify.template.emptyTitle')}
                description={
                  isFiltered ? t('dataTable.emptyFilteredHint') : t('notify.template.emptyHint')
                }
                {...(isFiltered
                  ? { actionLabel: t('dataTable.resetFilters'), onAction: resetTable }
                  : {})}
              />
            ),
          }}
        />
      </Card>

      <TemplateFormModal
        open={formOpen}
        group={editing}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
